import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'card', 'recap', 'recapMainTitle', 'recapExtras',
    'extrasList', 'mainTemplateInput', 'overlay'
  ];

  static values = {
    mainTemplateId: { type: Number, default: 0 },
    extraTemplateIds: { type: Array, default: [] }
  };

  static outlets = ['bd-reader'];

  connect() {
    // Back-nav support: rehydrate from DOM attributes rendered by the server
    this.mainTemplateIdValue = Number(this.element.dataset.initialMainId || 0);
    this.extraTemplateIdsValue = JSON.parse(this.element.dataset.initialExtraIds || '[]');
  }

  selectMain(event) {
    const id = Number(event.currentTarget.closest('[data-bd-template-id]').dataset.bdTemplateId);
    if (this.mainTemplateIdValue === id) {
      this.resetAll();
    } else {
      this.mainTemplateIdValue = id;
      this.extraTemplateIdsValue = this.extraTemplateIdsValue.filter((x) => x !== id);
    }
  }

  toggleExtra(event) {
    const id = Number(event.currentTarget.closest('[data-bd-template-id]').dataset.bdTemplateId);
    this.extraTemplateIdsValue = this.extraTemplateIdsValue.includes(id)
      ? this.extraTemplateIdsValue.filter((x) => x !== id)
      : [...this.extraTemplateIdsValue, id];
  }

  resetAll() {
    this.mainTemplateIdValue = 0;
    this.extraTemplateIdsValue = [];
  }

  // Value-changed callbacks drive everything — no manual wiring
  mainTemplateIdValueChanged() {
    if (this.hasMainTemplateInputTarget) {
      this.mainTemplateInputTarget.value = this.mainTemplateIdValue || '';
    }
    this.render();
  }

  extraTemplateIdsValueChanged() {
    this.syncHiddenInputs();
    this.render();
  }

  render() {
    this.cardTargets.forEach((card) => {
      const id = Number(card.dataset.bdTemplateId);
      card.classList.toggle('is-selected', id === this.mainTemplateIdValue);
      card.classList.toggle(
        'is-upsell',
        this.mainTemplateIdValue !== 0 && id !== this.mainTemplateIdValue
      );
      card.classList.toggle('is-added', this.extraTemplateIdsValue.includes(id));
    });
    this.updateRecap();
  }

  updateRecap() {
    const hasSelection = this.mainTemplateIdValue !== 0;
    this.element.classList.toggle('has-selection', hasSelection);
    if (this.hasRecapTarget) {
      this.recapTarget.classList.toggle('is-visible', hasSelection);
    }

    if (!hasSelection) return;

    if (this.hasRecapMainTitleTarget) {
      const mainCard = this.cardTargets.find(
        (c) => Number(c.dataset.bdTemplateId) === this.mainTemplateIdValue
      );
      this.recapMainTitleTarget.textContent = mainCard ? mainCard.dataset.bdTitle : '';
    }

    if (this.hasRecapExtrasTarget) {
      const titles = this.extraTemplateIdsValue
        .map((id) => this.cardTargets.find((c) => Number(c.dataset.bdTemplateId) === id))
        .filter(Boolean)
        .map((c) => c.dataset.bdTitle);
      const count = titles.length;
      this.recapExtrasTarget.textContent = count > 0
        ? `+ ${count} BD supplémentaire${count > 1 ? 's' : ''} : ${titles.join(', ')}`
        : '';
    }
  }

  syncHiddenInputs() {
    if (!this.hasExtrasListTarget) return;
    this.extrasListTarget.innerHTML = '';
    this.extraTemplateIdsValue.forEach((id) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'extra_template_ids[]';
      input.value = id;
      this.extrasListTarget.appendChild(input);
    });
  }

  openReader(event) {
    const card = event.currentTarget.closest('[data-bd-template-id]');
    if (!card || !this.hasBdReaderOutlet) return;
    this.bdReaderOutlet.open(card.dataset.excerptPages, {
      title: card.dataset.bdTitle || '',
      author: card.dataset.bdAuthor || ''
    });
  }

  // Called by bd-reader (via outlet) when the user clicks "Je la
  // choisis" on the last spread of the reader. Looks up the source
  // card by `data-bd-title` and triggers the same selection flow as
  // clicking the card's main CTA.
  chooseFromReader(bdName) {
    if (!bdName) return;
    const card = this.cardTargets.find((c) => c.dataset.bdTitle === bdName);
    if (!card) return;
    const id = Number(card.dataset.bdTemplateId);
    if (!Number.isInteger(id) || id <= 0) return;
    if (this.mainTemplateIdValue === id) return;
    this.mainTemplateIdValue = id;
    this.extraTemplateIdsValue = this.extraTemplateIdsValue.filter((x) => x !== id);
  }

  showOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.classList.add('visible');
  }
}
